import React, { useState } from 'react';
import { StandardSection, createEntitySectionConfig } from '../common';
import type { TableColumn } from '../common';
import type { Pozo, Pad, Operadora, Equipo } from '../../types';
import { pozoFormFields } from '../../config/formFields';

interface PozosSectionProps {
  data: {
    pozos: Pozo[];
    pads: Pad[];
    operadoras: Operadora[];
    equipos: Equipo[];
  };
  operations: {
    handlePozoSubmit: (data: Record<string, any>) => Promise<void>;
    handlePozoDelete: (item: Pozo) => Promise<void>;
    handlePozoEdit: (item: Pozo, key: string, newValue: any) => Promise<void>;
  };
  userPermissions: {
    canEditReports: boolean;
    canDeleteReports: boolean;
  };
}

export const PozosSection: React.FC<PozosSectionProps> = ({ data, operations, userPermissions }) => {
  // Filter state for custom view: Operadora → Equipo → Pad → Pozos
  const [selectedOperadoraId, setSelectedOperadoraId] = useState<string>('');
  const [selectedEquipoId, setSelectedEquipoId] = useState<string>('');
  const [selectedPadId, setSelectedPadId] = useState<string>('');
  const [newPozoName, setNewPozoName] = useState<string>('');
  const [newComprobanteBase, setNewComprobanteBase] = useState<string>('');

  // Editing state
  const [editingPozoId, setEditingPozoId] = useState<string | null>(null);
  const [editingNombre, setEditingNombre] = useState<string>('');
  const [editingComprobanteBase, setEditingComprobanteBase] = useState<string>('');

  // Filter equipos based on operadora selection (only equipos that have pads with this operadora)
  const filteredEquipos = selectedOperadoraId
    ? data.equipos.filter(eq => eq.habilitado && eq.operadoraId === selectedOperadoraId)
    : [];

  // Filter pads based on exact operadora+equipo combination
  const filteredPads = (selectedOperadoraId && selectedEquipoId)
    ? data.pads.filter(pad =>
        pad.operadoraId === selectedOperadoraId && pad.equipoId === selectedEquipoId)
    : [];

  // Filter pozos based on exact pad selection and sort alphabetically by name
  const filteredPozos = selectedPadId
    ? data.pozos
        .filter(pozo => pozo.padId === selectedPadId)
        .sort((a, b) => a.nombre.localeCompare(b.nombre))
    : [];

  // Handle new pozo creation
  const handleAddPozo = async () => {
    if (!newPozoName.trim() || !selectedPadId) {
      alert('⚠️ Debes seleccionar un pad y escribir un nombre para el pozo');
      return;
    }

    try {
      await operations.handlePozoSubmit({
        nombre: newPozoName.trim(),
        padId: selectedPadId,
        comprobanteBase: newComprobanteBase.trim() || undefined
      });
      setNewPozoName('');
      setNewComprobanteBase('');
      alert('✅ Pozo agregado exitosamente');
    } catch (error) {
      console.error('Error adding pozo:', error);
      alert('❌ Error al agregar pozo');
    }
  };

  // Start editing a pozo
  const handleStartEdit = (pozo: Pozo) => {
    setEditingPozoId(pozo.id);
    setEditingNombre(pozo.nombre);
    setEditingComprobanteBase(pozo.comprobanteBase || '');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPozoId(null);
    setEditingNombre('');
    setEditingComprobanteBase('');
  };

  // Save edits
  const handleSaveEdit = async (pozo: Pozo) => {
    try {
      if (editingNombre !== pozo.nombre) {
        await operations.handlePozoEdit(pozo, 'nombre', editingNombre);
      }
      if (editingComprobanteBase !== (pozo.comprobanteBase || '')) {
        await operations.handlePozoEdit(pozo, 'comprobanteBase', editingComprobanteBase);
      }
      setEditingPozoId(null);
      setEditingNombre('');
      setEditingComprobanteBase('');
      alert('✅ Pozo actualizado exitosamente');
    } catch (error) {
      console.error('Error updating pozo:', error);
      alert('❌ Error al actualizar pozo');
    }
  };

  // Table column configurations
  const pozoColumns: TableColumn<Pozo>[] = [
    {
      key: 'nombre',
      title: 'Nombre del Pozo',
      width: '20%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePozoEdit
    },
    {
      key: 'comprobanteBase',
      title: 'Cód. Base Comprobante',
      width: '25%',
      editable: true,
      editType: 'text',
      onEdit: operations.handlePozoEdit,
      render: (value) => value || '(No establecido)'
    },
    {
      key: 'padId',
      title: 'Pad / Locación',
      width: '20%',
      editable: true,
      editType: 'select',
      selectOptions: data.pads.map(pad => ({ value: pad.id, label: pad.nombre })),
      onEdit: operations.handlePozoEdit,
      render: (value) => {
        const pad = data.pads.find(p => p.id === value);
        return pad?.nombre || '(Sin asignar)';
      }
    }
  ];

  // Quick Setup Handler for standard pozos
  const handlePozosQuickSetup = async () => {
    if (data.pads.length === 0) {
      alert('⚠️ Primero debes crear pads antes de cargar pozos.');
      return;
    }

    if (window.confirm('¿Cargar pozos estándar? Esta acción creará pozos de ejemplo para cada pad registrado.')) {
      const pozoTemplates = [
        'Pozo Exploratorio', 'Pozo Desarrollo', 'Pozo Inyector',
        'Pozo Horizontal', 'Pozo Vertical', 'Pozo Productor'
      ];

      try {
        for (let i = 0; i < Math.min(data.pads.length, 10); i++) {
          const pad = data.pads[i];
          const operadora = data.operadoras.find(op => op.id === pad.operadoraId);

          await operations.handlePozoSubmit({
            nombre: `${pozoTemplates[i % pozoTemplates.length]} ${i + 1} - ${operadora?.nombre?.split(' ')[0] || 'OP'}`,
            padId: pad.id
          });
        }
        alert('✅ Pozos estándar cargados exitosamente');
      } catch (error) {
        console.error('Error cargando pozos:', error);
        alert('❌ Error cargando pozos. Ver consola para detalles.');
      }
    }
  };

  return (
    <>
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h2>🕳️ Gestión de Pozos</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Administra los pozos de perforación y sus asignaciones a pads/locaciones
        </p>
      </div>

      {/* Custom Filtered Pozos Section */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ color: '#333', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🕳️ Pozos de Perforación
        </h3>

        {/* Cascading Filter Controls */}
        <div style={{
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          {/* Step 1: Operadora Selector (Always visible) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>1. Seleccionar Operadora:</label>
            <select
              value={selectedOperadoraId}
              onChange={(e) => {
                setSelectedOperadoraId(e.target.value);
                setSelectedEquipoId(''); // Reset equipo when operadora changes
                setSelectedPadId(''); // Reset pad when operadora changes
                setNewPozoName(''); // Reset pozo name
                setNewComprobanteBase(''); // Reset comprobante base
              }}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                minWidth: '180px'
              }}
            >
              <option value="">Seleccionar operadora...</option>
              {data.operadoras.filter(op => op.habilitada).map(operadora => (
                <option key={operadora.id} value={operadora.id}>
                  {operadora.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Step 2: Equipo Selector (Only appears when operadora is selected) */}
          {selectedOperadoraId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>2. Seleccionar Equipo:</label>
              <select
                value={selectedEquipoId}
                onChange={(e) => {
                  setSelectedEquipoId(e.target.value);
                  setSelectedPadId(''); // Reset pad when equipo changes
                  setNewPozoName(''); // Reset pozo name
                  setNewComprobanteBase(''); // Reset comprobante base
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
              >
                <option value="">Seleccionar equipo...</option>
                {filteredEquipos.map(equipo => (
                  <option key={equipo.id} value={equipo.id}>
                    {equipo.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3: Pad Selector (Only appears when equipo is selected) */}
          {selectedOperadoraId && selectedEquipoId && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>3. Seleccionar Pad:</label>
              <select
                value={selectedPadId}
                onChange={(e) => {
                  setSelectedPadId(e.target.value);
                  setNewPozoName(''); // Reset pozo name when pad changes
                  setNewComprobanteBase(''); // Reset comprobante base when pad changes
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '180px'
                }}
              >
                <option value="">Seleccionar pad...</option>
                {filteredPads.map(pad => (
                  <option key={pad.id} value={pad.id}>
                    {pad.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 4: Add New Pozo Fields (Only appears when pad is selected) */}
          {selectedPadId && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>4. Nombre del Pozo:</label>
                <input
                  type="text"
                  value={newPozoName}
                  onChange={(e) => setNewPozoName(e.target.value)}
                  placeholder="Nombre del pozo"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPozo()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '200px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Código Base Comprobante (opcional):</label>
                <input
                  type="text"
                  value={newComprobanteBase}
                  onChange={(e) => setNewComprobanteBase(e.target.value)}
                  placeholder="ej: NQN-CS-03-0144 B"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPozo()}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    minWidth: '250px'
                  }}
                />
              </div>
              <button
                onClick={handleAddPozo}
                disabled={!newPozoName.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newPozoName.trim() ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newPozoName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  marginTop: '18px'
                }}
              >
                ➕ Agregar
              </button>
            </>
          )}
        </div>

        {/* Filtered Pozos List (Only appears when pad is selected) */}
        {selectedPadId && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              paddingBottom: '10px',
              borderBottom: '1px solid #eee'
            }}>
              <h4 style={{ color: '#333', margin: 0 }}>
                Pozos existentes en {data.pads.find(p => p.id === selectedPadId)?.nombre} ({data.operadoras.find(op => op.id === selectedOperadoraId)?.nombre} - {data.equipos.find(eq => eq.id === selectedEquipoId)?.nombre})
              </h4>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {filteredPozos.length} pozo{filteredPozos.length !== 1 ? 's' : ''}
              </span>
            </div>

            {filteredPozos.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '10px'
              }}>
                {filteredPozos.map(pozo => {
                  const isEditing = editingPozoId === pozo.id;
                  return (
                    <div key={pozo.id} style={{
                      padding: '15px',
                      backgroundColor: isEditing ? '#fffbf0' : '#f8f9fa',
                      borderRadius: '8px',
                      border: isEditing ? '2px solid #FF9800' : '1px solid #e9ecef',
                      transition: 'all 0.2s'
                    }}>
                      <div style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: '#333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingNombre}
                            onChange={(e) => setEditingNombre(e.target.value)}
                            style={{
                              flex: 1,
                              padding: '4px 8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                            placeholder="Nombre del pozo"
                          />
                        ) : (
                          <span>🕳️ {pozo.nombre}</span>
                        )}
                        <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(pozo)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#4CAF50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Guardar cambios"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#666',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Cancelar"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleStartEdit(pozo)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Editar pozo"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => operations.handlePozoDelete(pozo)}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Eliminar pozo"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        📍 {data.pads.find(p => p.id === pozo.padId)?.nombre}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        🏢 {data.operadoras.find(op => op.id === selectedOperadoraId)?.nombre}
                      </div>
                      {isEditing ? (
                        <div style={{ marginTop: '12px' }}>
                          <label style={{
                            display: 'block',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#666',
                            marginBottom: '4px'
                          }}>
                            Código Base Comprobante:
                          </label>
                          <input
                            type="text"
                            value={editingComprobanteBase}
                            onChange={(e) => setEditingComprobanteBase(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '4px 8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                            placeholder="ej: NQN-CS-03-0144 B"
                          />
                        </div>
                      ) : (
                        pozo.comprobanteBase && (
                          <div style={{
                            fontSize: '11px',
                            color: '#666',
                            backgroundColor: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            marginTop: '4px'
                          }}>
                            📋 {pozo.comprobanteBase}
                          </div>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#666',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #ddd'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🕳️</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                  No hay pozos en este pad
                </div>
                <div style={{ fontSize: '14px' }}>
                  Agrega el primer pozo usando el campo de arriba
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Setup Button */}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            onClick={handlePozosQuickSetup}
            style={{
              padding: '10px 20px',
              backgroundColor: '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🕳️ Cargar Pozos Estándar
          </button>
        </div>
      </div>

      {/* Summary Section */}
      <div style={{
        marginTop: '30px',
        padding: '20px',
        backgroundColor: '#e8f5e8',
        borderRadius: '12px',
        border: '2px solid #4CAF50'
      }}>
        <h3 style={{ color: '#2E7D32', marginBottom: '15px' }}>📊 Resumen de Pozos</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginTop: '15px'
        }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🕳️ Total Pozos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              {data.pozos.length}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📍 Pads con Pozos
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#FF9800'
            }}>
              {new Set(data.pozos.map(pozo => pozo.padId)).size}
            </div>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: 'white',
            borderRadius: '8px',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
              🏢 Operadoras
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#2196F3'
            }}>
              {new Set(data.pozos.map(pozo => {
                const pad = data.pads.find(p => p.id === pozo.padId);
                return pad?.operadoraId;
              }).filter(Boolean)).size}
            </div>
          </div>
        </div>

        {/* Pozos por Pad */}
        {data.pads.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h4 style={{ color: '#2E7D32', marginBottom: '10px' }}>Pozos por Pad:</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px' }}>
              {data.pads.map(pad => {
                const padPozos = data.pozos.filter(pozo => pozo.padId === pad.id);
                const operadora = data.operadoras.find(op => op.id === pad.operadoraId);
                return (
                  <div key={pad.id} style={{
                    padding: '15px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #ddd'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2E7D32' }}>
                      📍 {pad.nombre}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      {operadora?.nombre || 'Sin operadora'}
                    </div>
                    {padPozos.length > 0 ? (
                      <div style={{ fontSize: '14px' }}>
                        {padPozos
                          .sort((a, b) => a.nombre.localeCompare(b.nombre))
                          .map(pozo => (
                            <div key={pozo.id} style={{
                              marginBottom: '4px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>🕳️ {pozo.nombre}</span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div style={{ color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                        Sin pozos perforados
                      </div>
                    )}
                    <div style={{ fontSize: '12px', color: '#4CAF50', marginTop: '8px', fontWeight: 'bold' }}>
                      {padPozos.length} pozo{padPozos.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.pozos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay pozos registrados. Primero crea operadoras, equipos y pads, luego usa la carga rápida.
          </div>
        )}
      </div>
    </>
  );
};